import VormatsAPI from 'common/apis/VormatsAPI';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import styles from './Colors.module.scss';
import { useUserContext } from '../../../../context/UserContext';
import Icon from '../../../../components/Icon/Icon';
import { useTranslation } from 'react-i18next';
import DotsBtn from '../../../../components/Menu/DotsBtn';
import Menu from '../../../../components/Menu/Menu';
import MenuItem from '../../../../components/Menu/MenuItem';
import useFixedCardMenu from '../../../../features/Editor/hooks/useFixedCardMenu';
import { CustomButton } from '../../../../components/Button/Button';
import { HexAlphaColorPicker, HexColorInput } from 'react-colorful';
import { ToastNotification } from '../../../../components/Toast/Toast';
import ScrollContainer from '../../../../components/ScrollContainer/ScrollContainer';
import CloseButton from '../../../../components/CloseButton/CloseButton';
import Modal from '../../../../components/Modal';
import Spinner from '../../../../../common/components/Spinner';
import { useAccordion } from '../../hooks/useAccordion';
import { BrandingAccordionHeader } from '../BrandingAccordionHeader/BrandingAccordionHeader';

export const Colors = ({ companyId, updateColors }) => {
    const [loading, setLoading] = useState(false);
    const [addColorModal, setAddColorModal] = useState(false);
    const [newColor, setNewColor] = useState('');
    const [colors, setColors] = useState([]);

    const scrollContainerRef = useRef(null);

    const { isActive, contentRef, toggleAccordionState } = useAccordion(true);

    const { menuRef, showMenu, menuCoordinates, onOpenMenu, toggleShowMenu } = useFixedCardMenu({ containerRef: scrollContainerRef, offsetX: 200, offsetY: 200 });

    const { t } = useTranslation();

    const { isSuperAdmin } = useUserContext();

    useEffect(() => {
        const fetchColors = async () => {
            setLoading(true);
            try {
                const colors = await VormatsAPI.getCompanyColors(isSuperAdmin ? companyId : null);
                setColors(colors);
            } catch (error) {
                console.error('Error fetching colors:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchColors();
    }, [companyId]);

    const handleRemoveColor = useCallback(async (id) => {
        const confirm = window.confirm(t('company_settings.colors.delete_confirm'));
        if (confirm) {
            try {
                setLoading(true);
                await VormatsAPI.removeCompanyColor(id, isSuperAdmin ? companyId : null);
                setColors(colors.filter(color => color.id !== id));
            } catch (error) {
                console.error('Error removing color:', error);
            } finally {
                setLoading(false);
            }
        }
    }, [colors, companyId, isSuperAdmin, t]);

    const handleCloseColorModal = () => {
        setAddColorModal(false);
        setNewColor('');
    }

    const handleAddColor = useCallback(async () => {
        if (!newColor) return;

        try {
            setLoading(true);
            const color = await VormatsAPI.addCompanyColor(newColor, isSuperAdmin ? companyId : null);
            setColors([...colors, color]);
            scrollContainerRef.current?.scrollTo({
                left: scrollContainerRef.current.scrollWidth,
                behavior: 'smooth',
            });
        } catch (error) {
            console.error('Error adding color:', error);
            ToastNotification({
                type: 'error',
                message: t('company_settings.colors.add_error'),
            })
        } finally {
            setLoading(false);
            handleCloseColorModal();
        }
    }, [newColor, companyId, isSuperAdmin, colors, t]);

    useEffect(() => {
        scrollContainerRef.current?.scrollTo({
            left: 0,
            behavior: 'smooth',
        });
    }, [companyId]);

    useEffect(() => {
        updateColors(colors);
    }, [colors])

    return (
        <div className={styles.wrapper}>
            <BrandingAccordionHeader isActive={isActive} onClick={toggleAccordionState} title={`${t('company_settings.colors.title')} (${colors.length})`} description={t('company_settings.branding.colors_description')} />
            <div ref={contentRef} style={{ overflow: 'hidden' }}>
                <ScrollContainer className={cx(styles.scrollContainer, { [styles.scrollContainerDisabled]: loading })} innerRef={scrollContainerRef}>
                    <div key={'add-new-color'} className={cx(styles.colorItem, styles.addColorItem)} role='button' onClick={() => setAddColorModal(true)}>
                        <Icon src='plus' className={styles.addColorIcon} />
                        <span>{t('company_settings.colors.new')}</span>
                    </div>
                    {colors?.map(({ id, color }) => (
                        <div key={id} className={styles.colorItem} style={{ backgroundColor: color }}>
                            <div className={styles.colorInfo}>
                                <span className={styles.colorCode}>{color}</span>
                                <DotsBtn className={styles.dotsBtn} onClick={(event) => onOpenMenu({ id, event })} />
                            </div>
                        </div>
                    ))}
                </ScrollContainer>
            </div>
            {loading && isActive && <Spinner width="40px" height="40px" size="4px" className={styles.spinner} />}
            {showMenu.flag && <Menu ref={menuRef} customStyles={{ width: 'fit-content', top: menuCoordinates.top, left: menuCoordinates.left, transform: menuCoordinates.transform }} onClose={toggleShowMenu}>
                <MenuItem onClick={() => { toggleShowMenu(null); handleRemoveColor(showMenu.id); }} className={styles.menuItem}>
                    <Icon src='bin_red' className={styles.binActionIcon} />
                    <span style={{ color: 'rgb(203, 0, 0)' }}>{t('general.video_action_button.delete')}</span>
                </MenuItem>
            </Menu>}
            <Modal open={addColorModal} onClose={handleCloseColorModal} className={styles.addColorModal}>
                <div className={styles.modalWrapper}>
                    <div className={styles.modalTitle}>
                        <span>{t('company_settings.colors.new')}</span>
                        <CloseButton onClose={handleCloseColorModal} />
                    </div>
                    <div className={styles.colorPickerContainer}>
                        <HexAlphaColorPicker color={newColor || ''} onChange={setNewColor} />
                        <div className={styles.colorInputContainer}>
                            <span className={styles.colorInputLabel}>{t('company_settings.colors.new_action')}</span>
                            <HexColorInput alpha={true} prefixed={true} color={newColor || ''} onChange={setNewColor} />
                        </div>
                    </div>
                    <div className={styles.actionsContainer}>
                        <CustomButton className={styles.actionBtn} variant='secondary' label={t('general.cancel')} onClick={handleCloseColorModal} />
                        <CustomButton className={styles.actionBtn} variant='primary' label={t('general.save')} onClick={handleAddColor} />
                    </div>
                </div>
            </Modal>
        </div>
    );
}